import { memoryTokenStore } from "@brandex/api-client";
import { AuthGate, BrandexProvider, LoginForm, useSignOut, type LinkProps, type Navigation, type Platform } from "@brandex/dashboard";
import { Alert, Button, Spinner, initTheme } from "@brandex/ui";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link as RouterLink,
  Navigate,
  Outlet,
  RouterProvider,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";

import { useStaffAccess } from "./access";
import { AdminLayout } from "./layout";
import { canOpen, homeFor } from "./nav";
import { AuthCard, ForgotPasswordPage, PasswordLinkPage } from "./pages/account";
import { ContentPage } from "./pages/content";
import { SocialHealthPage } from "./pages/social-health";
import { ArticlesPage, ID } from "./pages/articles";
import { StaffPage, isStaffTab } from "./pages/staff";
import { ClientNewPage } from "./pages/client-new";
import { ClientManagePage, isManageTab, type ManageTab } from "./pages/client-manage";
import { ClientReportPage } from "./pages/client-report";
import { ClientsPage } from "./pages/clients";
import { TemplatesPage } from "./pages/templates";
import { VerificationPage } from "./pages/verification";
import { CmsPage } from "./pages/cms";
import { PackagesPage } from "./pages/packages";
import { SubscriptionsPage } from "./pages/subscriptions";
import { BookingsPage } from "./pages/bookings";
import { OverviewPage } from "./pages/overview";
import { PaymentsPage } from "./pages/payments";
import { PipelinePage } from "./pages/pipeline";
import { PublishingPage } from "./pages/publishing";
import { SchedulePage } from "./pages/schedule";
import { WebsitesPage } from "./pages/websites";
import { AuditPage, DashboardPage, OrganizationsPage, UsersPage } from "./views";

import "./styles.css";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:8080";
const WEB_BASE_URL = (import.meta.env.VITE_WEB_BASE_URL as string | undefined) ?? "http://localhost:3000";

/** Admin client: in-memory access token, httpOnly refresh cookie (client=admin). */
const platform: Platform = {
  kind: "web",
  client: "admin",
  tokens: memoryTokenStore(),
  notify: () => Promise.resolve(),
  openExternal: (url) => {
    if (/^https?:\/\//.test(url)) window.open(url, "_blank", "noopener,noreferrer");
    return Promise.resolve();
  },
};

function Link({ href, className, children, ...rest }: LinkProps) {
  return <RouterLink to={href} className={className} aria-current={rest["aria-current"]}>{children}</RouterLink>;
}

function Root() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useMemo<Navigation>(
    () => ({
      pathname,
      // The shared dashboard redirects to /auth/login; the admin login lives at /login.
      navigate: (href) => void navigate({ to: href === "/auth/login" ? "/login" : href }),
      Link,
    }),
    [navigate, pathname],
  );
  // siteUrl: doctor sites live on the web app, not on this panel's origin.
  return (
    <BrandexProvider apiBaseUrl={API_BASE_URL} platform={platform} nav={nav} initialLocale="en" siteUrl={WEB_BASE_URL.replace(/\/$/, "")}>
      <Outlet />
    </BrandexProvider>
  );
}


function AdminShell() {
  return (
    <AuthGate>
      <StaffOnly />
    </AuthGate>
  );
}

/** Signed in: staff get the panel; anyone else is told where they belong. */
function StaffOnly() {
  const access = useStaffAccess();
  const signOut = useSignOut();
  if (access.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }
  if (access.isError || !access.data.is_staff) {
    return (
      <AuthCard title="Not a staff account" lead="The admin panel is for the BrandEx team. Doctors and their assistants use the BrandEx web app.">
        <div className="flex flex-wrap gap-2">
          <Button asChild><a href={`${WEB_BASE_URL.replace(/\/$/, "")}/auth/login`}>Open the web app</a></Button>
          <Button variant="outline" onClick={() => signOut.mutate(false)}>Sign out</Button>
        </div>
      </AuthCard>
    );
  }
  return <Panel />;
}

/**
 * Pages render only when the staff member's access covers them (ADR 0022),
 * so nobody fires requests the server refuses. `/` (Overview) needs every
 * client; staff limited to assigned clients land on their first page.
 */
function Guarded() {
  // The page the outlet renders now. During a navigation the address changes
  // first while the previous page stays on screen, so checking the address
  // would let that page through for a moment.
  const pathname = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.pathname ?? s.location.pathname });
  const access = useStaffAccess();
  if (canOpen(access.data, pathname)) return <Outlet />;
  const home = homeFor(access.data);
  if (pathname === "/" && home !== null) return <Navigate to={home} replace />;
  return (
    <Alert tone="info">
      <span className="flex items-start gap-2">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {home === null
            ? "Your account has no staff permissions yet. Ask a super admin to give you a role."
            : "This page isn't part of your role. Use the menu to open the pages you have access to."}
        </span>
      </span>
    </Alert>
  );
}

function Panel() {
  return (
    <AdminLayout>
      <Guarded />
    </AdminLayout>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  return (
    <AuthCard title="Staff sign in" lead="Restricted to BrandEx staff. Two-factor authentication is required.">
      <LoginForm onSuccess={() => void navigate({ to: "/" })} />
      <RouterLink to="/auth/forgot-password" className="mt-4 inline-block text-sm text-muted-foreground hover:text-foreground hover:underline">
        Forgot your password?
      </RouterLink>
    </AuthCard>
  );
}

const rootRoute = createRootRoute({ component: Root });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
// Emailed staff links (ADR 0022) use the same paths as the web app's.
const setupPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/auth/setup-password", component: () => <PasswordLinkPage mode="setup" /> });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/auth/reset-password", component: () => <PasswordLinkPage mode="reset" /> });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/auth/forgot-password", component: ForgotPasswordPage });
const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: "shell", component: AdminShell });
/** `/clients/{orgId}/report`: the id is the path's second segment. */
/** The client id in `/clients/{orgId}/…`, or null while the address has none. */
function useClientId(): string | null {
  return useRouterState({ select: (s) => /^\/clients\/([0-9a-f]{24})(?:\/|$)/.exec(s.location.pathname)?.[1] ?? null });
}

function ClientReport() {
  const orgId = useClientId();
  if (orgId === null) return null;
  return <ClientReportPage orgId={orgId} />;
}

/** Only well-formed ids reach the Articles page. */
function parseArticlesSearch(s: Record<string, unknown>): { org?: string; edit?: string } {
  return {
    ...(typeof s.org === "string" && ID.test(s.org) ? { org: s.org } : {}),
    ...(typeof s.edit === "string" && (s.edit === "new" || ID.test(s.edit)) ? { edit: s.edit } : {}),
  };
}

/** `/articles?org=<id>&edit=<id|new>`: the open article stays in the address (refresh, back button, shared links). */
function ArticlesRoute() {
  const raw = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const search = parseArticlesSearch(raw);
  const navigate = useNavigate();
  return (
    <ArticlesPage
      org={search.org}
      edit={search.edit}
      go={(to, replace) => void navigate({ to: "/articles", search: to, ...(replace === true ? { replace: true } : {}) })}
    />
  );
}

const articlesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/articles",
  component: ArticlesRoute,
  validateSearch: parseArticlesSearch,
});

const clientReportRoute = createRoute({ getParentRoute: () => shellRoute, path: "/clients/$orgId/report", component: ClientReport });

/** `/clients/{orgId}/manage?tab=profile|articles|social|brand|bookings|website|visitors` (Social media keeps `platform`). */
function ClientManage() {
  const orgId = useClientId();
  const raw = useRouterState({ select: (s) => (s.location.search as Record<string, unknown>).tab });
  const navigate = useNavigate();
  if (orgId === null) return null;
  return (
    <ClientManagePage
      orgId={orgId}
      tab={isManageTab(raw) ? raw : "profile"}
      go={(tab, org) => void navigate({ to: "/clients/$orgId/manage", params: { orgId: org ?? orgId }, search: { tab } })}
    />
  );
}

const clientManageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/clients/$orgId/manage",
  component: ClientManage,
  validateSearch: (s: Record<string, unknown>): { tab?: ManageTab; platform?: string } => ({
    ...(isManageTab(s.tab) ? { tab: s.tab } : {}),
    ...(typeof s.platform === "string" ? { platform: s.platform } : {}),
  }),
});

/** `/staff?tab=staff|roles`. */
function StaffRoute() {
  const raw = useRouterState({ select: (s) => (s.location.search as Record<string, unknown>).tab });
  const navigate = useNavigate();
  return <StaffPage tab={isStaffTab(raw) ? raw : "staff"} go={(tab) => void navigate({ to: "/staff", search: { tab } })} />;
}

const staffRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/staff",
  component: StaffRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: "staff" | "roles" } => (isStaffTab(s.tab) ? { tab: s.tab } : {}),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupPasswordRoute,
  resetPasswordRoute,
  forgotPasswordRoute,
  shellRoute.addChildren([
    createRoute({ getParentRoute: () => shellRoute, path: "/", component: OverviewPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/platform", component: DashboardPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/clients", component: ClientsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/clients/new", component: ClientNewPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/content", component: ContentPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/social", component: SocialHealthPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/verification", component: VerificationPage }),
    clientReportRoute,
    clientManageRoute,
    staffRoute,
    createRoute({ getParentRoute: () => shellRoute, path: "/organizations", component: OrganizationsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/users", component: UsersPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/packages", component: PackagesPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/subscriptions", component: SubscriptionsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/websites", component: WebsitesPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/templates", component: TemplatesPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/pipeline", component: PipelinePage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/payments", component: PaymentsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/schedule", component: SchedulePage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/bookings", component: BookingsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/publishing", component: PublishingPage }),
    articlesRoute,
    createRoute({ getParentRoute: () => shellRoute, path: "/cms", component: CmsPage }),
    createRoute({ getParentRoute: () => shellRoute, path: "/audit", component: AuditPage }),
  ]),
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

initTheme();

const el = document.getElementById("root");
if (el !== null) {
  createRoot(el).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}
